// list of subscription plans
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  TrendingUp as AnalyticsIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PlanDetails from './PlanDetails';
import NewPlan from './NewPlan';
import PlanFilter from '../../components/common/PlanFilter';
import { formatCurrency } from '../../utils/helper';

const PlanList = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planTypeFilter, setPlanTypeFilter] = useState('all');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planDetailsOpen, setPlanDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  // Mock data for subscription plans
  const mockPlans = [
    {
      id: 1,
      name: 'Free Plan',
      plan_type: 'free',
      type: 'free',
      is_active: true,
      version: 1,
      per_user_cost: 0,
      per_credit_cost: 0,
      total_credits: 500,
      expiry_days: 30,
      insights_allowed: false,
      max_user_slots: 3,
      status: 'active',
      totalSubscribers: 15,
      totalRevenue: 0,
      createdAt: '2023-01-01',
      updatedAt: '2024-01-15',
      description: 'Perfect for small teams getting started'
    },
    {
      id: 2,
      name: 'Basic Plan',
      plan_type: 'basic',
      type: 'basic',
      is_active: true,
      version: 1,
      per_user_cost: 29.99,
      per_credit_cost: 5,
      total_credits: 2000,
      expiry_days: 30,
      insights_allowed: true,
      max_user_slots: 20,
      status: 'active',
      totalSubscribers: 25,
      totalRevenue: 749.75,
      createdAt: '2023-01-01',
      updatedAt: '2024-01-10',
      description: 'Ideal for growing businesses'
    },
    {
      id: 3,
      name: 'Advance Plan',
      plan_type: 'advance',
      type: 'advance',
      is_active: true,
      version: 1,
      per_user_cost: 99.99,
      per_credit_cost: 3,
      total_credits: 10000,
      expiry_days: 30,
      insights_allowed: true,
      max_user_slots: 100,
      status: 'active',
      totalSubscribers: 10,
      totalRevenue: 999.90,
      createdAt: '2023-01-01',
      updatedAt: '2024-01-05',
      description: 'For enterprise-level organizations'
    },
    {
      id: 4,
      name: 'Legacy Basic',
      plan_type: 'basic',
      type: 'basic',
      is_active: false,
      version: 1,
      per_user_cost: 24.99,
      per_credit_cost: 6,
      total_credits: 1500,
      expiry_days: 30,
      insights_allowed: false,
      max_user_slots: 15,
      status: 'deprecated',
      totalSubscribers: 5,
      totalRevenue: 124.95,
      createdAt: '2022-06-01',
      updatedAt: '2023-12-01',
      description: 'Legacy plan - no longer available for new subscribers'
    }
  ];

  useEffect(() => {
    setPlans(mockPlans);
  }, []);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
  };

  const handlePlanTypeFilter = (event) => {
    setPlanTypeFilter(event.target.value);
  };

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanDetailsOpen(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setNewPlanOpen(true);
  };

  const handleDeletePlan = (plan) => {
    setSelectedPlan(plan);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePlan = () => {
    setSnackbar({
      open: true,
      message: `Plan "${selectedPlan.name}" deleted successfully`,
      severity: 'success'
    });
    setDeleteDialogOpen(false);
    setSelectedPlan(null);
  };

  const handleSavePlan = async (planData) => {
    try {
      // Here you would typically save the plan to your backend
      console.log('Saving plan:', planData);
      
      setSnackbar({
        open: true,
        message: editingPlan ? 'Plan updated successfully' : 'Plan created successfully',
        severity: 'success'
      });
      
      setNewPlanOpen(false);
      setEditingPlan(null);
      
      // Optionally refresh the plan list
      // await refreshPlans();
      
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error saving plan',
        severity: 'error'
      });
    }
  };

  const handleCreateNewPlan = () => {
    setEditingPlan(null);
    setNewPlanOpen(true);
  };

  const handleViewAnalytics = (planId) => {
    navigate(`/subscription-plans/${planId}/analytics`);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'error',
      deprecated: 'warning',
      draft: 'default'
    };
    return colors[status] || 'default';
  };

  const getPlanTypeColor = (type) => {
    const colors = {
      free: 'default',
      basic: 'primary',
      advance: 'secondary'
    };
    return colors[type] || 'default';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredPlans = plans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plan.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || plan.status === statusFilter;
    const matchesType = planTypeFilter === 'all' || plan.type === planTypeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Subscription Plans
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNewPlan}
        >
          Create New Plan
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by plan name or description..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusFilter}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="deprecated">Deprecated</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <PlanFilter
                value={planTypeFilter}
                onChange={handlePlanTypeFilter}
                label="Plan Type"
                fullWidth={true}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {/* Handle refresh */}}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {/* Handle advanced filters */}}
                >
                  Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plan Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Per User Cost</TableCell>
                  <TableCell>Per Credit Cost</TableCell>
                  <TableCell>Max Users</TableCell>
                  <TableCell>Total Credits</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subscribers</TableCell>
                  <TableCell>Revenue</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPlans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {plan.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {plan.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.type.toUpperCase()}
                        color={getPlanTypeColor(plan.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {plan.per_user_cost === 0 ? 'Free' : formatCurrency(plan.per_user_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {plan.per_credit_cost === 0 ? 'Free' : formatCurrency(plan.per_credit_cost)}
                      </Typography>
                    </TableCell>
                    <TableCell>{plan.max_user_slots}</TableCell>
                    <TableCell>{plan.total_credits.toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip
                        label={plan.status.toUpperCase()}
                        color={getStatusColor(plan.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {plan.totalSubscribers}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(plan.totalRevenue)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewPlan(plan)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Plan">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditPlan(plan)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Analytics">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleViewAnalytics(plan.id)}
                          >
                            <AnalyticsIcon />
                          </IconButton>
                        </Tooltip>
                        {plan.status !== 'active' && (
                          <Tooltip title="Delete Plan">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeletePlan(plan)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Plan Details Dialog */}
      <Dialog
        open={planDetailsOpen}
        onClose={() => setPlanDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BusinessIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold">
              Plan Details
            </Typography>
          </Box>
          <Button onClick={() => setPlanDetailsOpen(false)} size="small">
            <CloseIcon />
          </Button>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          {selectedPlan && (
            <Grid container spacing={3}>
              {/* Left Column - Plan Information */}
              <Grid item xs={12} md={8}>
                {/* Basic Information */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <BusinessIcon color="primary" />
                      Basic Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Plan Name</Typography>
                        <Typography variant="body1" fontWeight="bold">{selectedPlan.name}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Plan Type</Typography>
                        <Chip
                          label={selectedPlan.type.toUpperCase()}
                          color={getPlanTypeColor(selectedPlan.type)}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Version</Typography>
                        <Typography variant="body1">v{selectedPlan.version || 1}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                        <Chip
                          label={selectedPlan.status.toUpperCase()}
                          color={getStatusColor(selectedPlan.status)}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Pricing Information */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <CreditCardIcon color="primary" />
                      Pricing Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Per User Cost</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formatCurrency(selectedPlan.per_user_cost || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Per Credit Cost</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {formatCurrency(selectedPlan.per_credit_cost || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Total Credits</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedPlan.total_credits?.toLocaleString() || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Expiry Days</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedPlan.expiry_days || 30} days
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Limits & Features */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <PeopleIcon color="primary" />
                      Limits & Features
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Max User Slots</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedPlan.max_user_slots || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Insights Allowed</Typography>
                        <Chip
                          label={selectedPlan.insights_allowed ? 'Yes' : 'No'}
                          color={selectedPlan.insights_allowed ? 'success' : 'default'}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column - Summary & Actions */}
              <Grid item xs={12} md={4}>
                <Card sx={{ position: 'sticky', top: 16 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <CheckCircleIcon color="primary" />
                      Plan Summary
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium">Total Subscribers:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedPlan.totalSubscribers || 0}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium">Total Revenue:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(selectedPlan.totalRevenue || 0)}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium">Created:</Typography>
                        <Typography variant="body2">
                          {selectedPlan.createdAt}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight="medium">Last Updated:</Typography>
                        <Typography variant="body2">
                          {selectedPlan.updatedAt}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0', 
          pt: 2, 
          px: 3, 
          pb: 3,
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Plan ID: {selectedPlan?.id}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={() => setPlanDetailsOpen(false)} variant="outlined">
              Close
            </Button>
            <Button 
              onClick={() => {
                setPlanDetailsOpen(false);
                handleEditPlan(selectedPlan);
              }} 
              variant="contained"
              startIcon={<EditIcon />}
            >
              Edit Plan
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* New Plan Modal */}
      <NewPlan
        open={newPlanOpen}
        onClose={() => {
          setNewPlanOpen(false);
          setEditingPlan(null);
        }}
        onSave={handleSavePlan}
        plan={editingPlan}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the plan "{selectedPlan?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeletePlan} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PlanList;